import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { acknowledgementService } from '../../services/apiService';
import Icon from '../AppIcon';
import Button from './Button';
import { cn } from '../../utils/cn';
import amberLogo from '../../../assets/images/PICL logo.jpg.jpeg';

const Sidebar = ({ isMobileOpen, setIsMobileOpen, isCollapsed, setIsCollapsed, onStartTour }) => {
  const location = useLocation();
  const { user, isAdmin, isCoordinator } = useAuth();
  const [conflictedCount, setConflictedCount] = useState(0);

  // Close sidebar on route change on mobile
  useEffect(() => {
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false);
    }
  }, [location.pathname, setIsMobileOpen]);

  // Fetch conflicted acknowledgements count for badge
  useEffect(() => {
    const fetchConflictedCount = async () => {
      // Only fetch if user is admin or coordinator
      if (!isAdmin && !isCoordinator) {
        setConflictedCount(0);
        return;
      }

      try {
        const response = await acknowledgementService.getConflicted(1, 1, '');
        const total = response?.meta?.total || 0;
        setConflictedCount(total);
      } catch (err) {
        // Silently fail - don't show error for badge count
        console.error('Failed to fetch conflicted count:', err);
        setConflictedCount(0);
      }
    };

    fetchConflictedCount();
    // Refresh count every 30 seconds
    const interval = setInterval(fetchConflictedCount, 30000);
    return () => clearInterval(interval);
  }, [isAdmin, isCoordinator, location.pathname]); // Refresh when navigating to conflicting vendors page

  // Navigation items with role-based access
  const navigationItems = useMemo(() => {
    const items = [
      { 
        section: 'Overview',
        items: [
          { label: 'Dashboard', path: '/dashboard-overview', icon: 'LayoutDashboard', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'MIS Report', path: '/mis-report', icon: 'FileBarChart', roles: ['ADMIN', 'COORDINATOR'] },
        ]
      },
      {
        section: 'Vendor Management',
        items: [
          { label: 'Vendor Master', path: '/vendor-master-list', icon: 'Building2', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'Approvals', path: '/vendor-approval-workflow', icon: 'CheckCircle', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'Vendor Regularization', path: '/vendor-regularization', icon: 'RefreshCw', roles: ['ADMIN'] },
          { label: 'Conflicting Vendors', path: '/conflicting-vendors', icon: 'AlertTriangle', roles: ['ADMIN', 'COORDINATOR'], badge: conflictedCount > 0 ? conflictedCount : null },
          { label: 'Requests', path: '/supplier-requests', icon: 'FileText', roles: ['ADMIN', 'COORDINATOR', 'USER'] },
        ]
      },
      {
        section: 'Administration',
        items: [
          { label: 'User Management', path: '/user-management', icon: 'Users', roles: ['ADMIN'] },
          { label: 'Invite Management', path: '/invite-management', icon: 'Mail', roles: ['ADMIN', 'COORDINATOR'] },
          { label: 'Excel Import', path: '/vendor-excel-import', icon: 'FileSpreadsheet', roles: ['ADMIN'] },
        ]
      }
    ];

    // Filter based on user role
    const userRole = user?.role?.toUpperCase();
    return items.map(group => ({
      ...group,
      items: group.items.filter(item => !item.roles || item.roles.includes(userRole))
    })).filter(group => group.items.length > 0);
  }, [user?.role, conflictedCount]);

  const isActivePath = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile Overlay */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300",
          isMobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
        onClick={() => setIsMobileOpen(false)}
      />

      {/* Sidebar Container */}
      <aside 
        id="tour-sidebar"
        className={cn(
          "fixed top-0 left-0 z-50 h-screen bg-slate-900 text-slate-300 transition-all duration-300 ease-in-out shadow-xl border-r border-slate-800",
          isCollapsed ? "w-16" : "w-64",
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-800 bg-slate-950/50">
          {!isCollapsed && (
            <Link to="/dashboard-overview" className="flex items-center space-x-3 overflow-hidden">
              <img src={amberLogo} alt="PICLLogo" className="flex-shrink-0 h-8 w-auto object-contain" />
              <span className="text-lg font-bold text-white tracking-tight whitespace-nowrap">PICL-VMS</span>
            </Link>
          )}
          {isCollapsed && (
            <Link to="/dashboard-overview" className="flex items-center justify-center w-full">
              <img src={amberLogo} alt="PICLLogo" className="h-8 w-auto object-contain max-w-[2.5rem]" />
            </Link>
          )}
          
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden lg:flex items-center justify-center w-6 h-6 rounded-md hover:bg-slate-800 text-slate-400 hover:text-white transition-colors absolute -right-3 top-1/2 -translate-y-1/2 border border-slate-700 bg-slate-900 shadow-sm"
          >
            <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={14} />
          </button>
        </div>

        {/* Navigation Content */}
        <div className="flex flex-col h-[calc((100vh/var(--app-scale))-4rem)] overflow-y-auto py-4 custom-scrollbar">
          <nav className="flex-1 space-y-6 px-3">
            {navigationItems.map((group, idx) => (
              <div key={idx}>
                {!isCollapsed && group.section && (
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 px-3">
                    {group.section}
                  </h3>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={isCollapsed ? item.label : ''}
                      className={cn(
                        "flex items-center px-3 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group relative",
                        isActivePath(item.path)
                          ? "bg-blue-600 text-white shadow-md shadow-blue-900/20"
                          : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
                      )}
                    >
                      <Icon 
                        name={item.icon} 
                        size={20} 
                        className={cn(
                          "flex-shrink-0 transition-colors",
                          isActivePath(item.path) ? "text-white" : "text-slate-400 group-hover:text-slate-300"
                        )} 
                      />
                      
                      {!isCollapsed && (
                        <span className="ml-3 truncate">{item.label}</span>
                      )}

                      {/* Badge */}
                      {item.badge && item.badge > 0 && (
                        <span className={cn(
                          "absolute bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px]",
                          isCollapsed 
                            ? "top-0 right-0 w-4 h-4 translate-x-1 -translate-y-1" 
                            : "right-2 px-1.5 py-0.5"
                        )}>
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Take a Tour */}
          {onStartTour && (
            <div className="px-3 py-2">
              <button
                type="button"
                onClick={onStartTour}
                className={cn(
                  "w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  "bg-slate-800/50 text-slate-300 hover:bg-slate-700 hover:text-white"
                )}
              >
                <Icon name="HelpCircle" size={18} />
                {!isCollapsed && <span>Take a Tour</span>}
              </button>
            </div>
          )}

          {/* User Info & Logout */}
          <div className="mt-auto px-3 border-t border-slate-800 pt-4">
            {!isCollapsed && user && (
              <div className="flex items-center px-3 py-2 mb-2">
                <div className="h-8 w-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-medium">
                  {user.name?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                </div>
                <div className="ml-3 overflow-hidden">
                  <p className="text-sm font-medium text-white truncate">{user.name || 'User'}</p>
                  <p className="text-xs text-slate-500 truncate">{user.role}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
